import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { motion } from 'motion/react'
import type { ReactNode } from 'react'
import { useUserData } from '~/api/queries/user'
import { extractIdFromIri } from '~/api/utils'
import { ProfilesList } from '~/components/Global/Elements/ProfilesList/ProfilesList'
import { profilesListStandardHeight } from '~/components/Global/Elements/ProfilesList/ProfilesList.css'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import type {
  ActionHistoryEventHead,
  ApplicantHistoryEvent
} from '~/domains/actions/types'
import { OrderTag } from '~/domains/orders/components/OrderTag/OrderTag'
import { orderTagFlushBottom } from '~/domains/orders/components/OrderTag/OrderTag.css'
import { UsersList } from '~/domains/users/components/UsersList'
import { formatFullDateTime, formatRelativeTime } from '~/utils/date'
import {
  historyItem,
  historyItemEmbedded,
  historyItemHeader,
  historyItemTime,
  historyItemTitle,
  historyItemTitleEmbedded,
  historyItemWithOrder
} from '../ApplicantHistory.css'
import { useHistoryContext } from '../HistoryContext'

interface BaseHistoryCardProps {
  event: ApplicantHistoryEvent
  icon?: IconDefinition
  title: ReactNode
  headerAction?: ReactNode
  children?: ReactNode
}

const getEventDateInfo = (event: ActionHistoryEventHead) => {
  const createdDate = new Date(event.created)
  const isScheduledDateDifferent =
    event.scheduledDate &&
    event.type !== 'availability_update' &&
    new Date(event.scheduledDate).getTime() !== createdDate.getTime()

  if (event.completionDate) {
    const completionDate = new Date(event.completionDate)

    let tooltipText = `${event.isCancelled ? 'Annulé' : 'Traité'} le ${formatFullDateTime(new Date(event.completionDate))}`
    if (isScheduledDateDifferent && event.scheduledDate) {
      tooltipText += `\nProgrammé le ${formatFullDateTime(new Date(event.scheduledDate))}`
    }

    return {
      displayTime: formatRelativeTime(completionDate),
      tooltipText
    }
  }

  if (isScheduledDateDifferent && event.scheduledDate) {
    const scheduledDate = new Date(event.scheduledDate)
    const tooltipText = `Programmé le ${formatFullDateTime(new Date(event.scheduledDate))}`

    return {
      displayTime: formatRelativeTime(scheduledDate),
      tooltipText
    }
  }

  return {
    displayTime: formatRelativeTime(createdDate),
    tooltipText: `Le ${formatFullDateTime(new Date(event.created))}`
  }
}

export const BaseHistoryCard = ({
  event,
  icon,
  title,
  headerAction,
  children
}: BaseHistoryCardProps) => {
  const { showProfilePictures, embedded } = useHistoryContext()
  const applicantIris = 'applicants' in event ? (event.applicants ?? []) : []
  const contactIris = 'contacts' in event ? (event.contacts ?? []) : []
  const applicantIds = applicantIris
    .map((iri) => extractIdFromIri(iri))
    .filter((id): id is number => id !== null)
  const contactIds = contactIris
    .map((iri) => extractIdFromIri(iri))
    .filter((id): id is number => id !== null)
  const userIri = event.assignedUser || event.createdBy
  const userId = extractIdFromIri(userIri)
  const completedById =
    typeof event.completedBy === 'object' &&
    event.completedBy !== undefined &&
    event.completedBy !== null
      ? event.completedBy.id
      : extractIdFromIri(event.completedBy)
  const { data: users } = useUserData(
    [userId, userId !== completedById ? completedById : null].filter(
      (id) => id !== null && id !== undefined
    )
  )
  const user = users?.find((u) => u.id === userId)
  const completionUser = users?.find((u) => u.id === completedById)
  const dateInfo = getEventDateInfo(event)

  let usersTooltipText = ''
  if (completionUser && completionUser !== user) {
    usersTooltipText = `Traité par ${completionUser?.firstname} ${completionUser?.lastname}\n`
    usersTooltipText += `Programmé pour ${user?.firstname} ${user?.lastname} `
  }

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, x: -20 },
        visible: { opacity: 1, x: 0 }
      }}
    >
      {event.order && (
        <OrderTag order={event.order} className={orderTagFlushBottom} />
      )}

      <div
        className={clsx(
          historyItem,
          embedded && historyItemEmbedded,
          event.order && historyItemWithOrder
        )}
      >
        <div className={historyItemHeader}>
          {showProfilePictures && (
            <ProfilesList
              applicantIds={applicantIds}
              contactIds={contactIds}
              maxProfilesCount={3}
              className={profilesListStandardHeight}
            />
          )}
          {icon && <FontAwesomeIcon icon={icon} />}
          <div
            className={clsx(
              historyItemTitle,
              embedded && historyItemTitleEmbedded
            )}
          >
            {title}
          </div>
          <div className={historyItemTime}>
            {headerAction}
            {user && (
              <UsersList
                users={[
                  user,
                  user !== completionUser ? completionUser : null
                ].filter((u) => u !== null && u !== undefined)}
                tooltipText={usersTooltipText}
              />
            )}
            <Tooltip text={dateInfo.tooltipText} direction="top">
              {dateInfo.displayTime}
            </Tooltip>
          </div>
        </div>
        {children}
      </div>
    </motion.div>
  )
}
